import React, { useCallback, useEffect, useMemo, useState } from 'react'
import MainBreadcrumb from '../../../../../component-controller-mod/component-serv/navigation-fs-cp/breadcrumb-nav-op/page-main-bcb/ts/main-bcb'
import "../../css/submit.css"
import SelectField from '../../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectField'
import SearchField from '../../../../../component-controller-mod/component-serv/input-fs-cp/search-field-op/ts/searchField'
import { batchDeleteCareerApplications, CareerApplicationItem, columns, deleteInboxItem, formatBytes, getCareerSubmitApi, getCareerSubmitPreviewApi, getDepartmentIndexApi, getJobLevelIndexApi, getJobTypeIndexApi, InboxRow, normalizeSearch, toBadgeVariant, TypeBussiness } from './submit.type'
import { Table, Pagination, Empty, Button, Confirmation } from '@core/uilibrary'
import { DeleteConfirmationIcon, EyeIcon, TrashIcon } from '../../../../../asset-controller-mod/asset-serv/icons/iconExporter'
import StatusBadge from '../../../../../component-controller-mod/component-serv/badge-fs-cp/dynamic-badge-op/ts/statusBadge'
import { useGlobalPopupProvider } from '../../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider'
import { useToast } from '../../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider'
import { useRightSidebar } from '../../../../../context-controller-mod/context-serv/global-provider/right-sidebar-provider/ts/right-sidebar-context'
import UrsbSubmit from './ursb-submit'
import { groupPermissionAccessCMSCareer } from '../../../../../context-controller-mod/context-serv/permission-provider/permissionExporter'
import CancelButton from '../../../../../component-controller-mod/component-serv/button-fs-cp/cancel-button-op/ts/cancelButton';

type Option = { value: number | string; label: string };

const SubmitUi = () => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<InboxRow[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const { openConfirm, closeConfirm } = useGlobalPopupProvider();
    const [saving, setSaving] = useState(false);
    const { showToast } = useToast();
    const ursb = useRightSidebar();
    const [reloadKey, setReloadKey] = useState(0);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [departmentOptions, setDepartmentOptions] = useState<Option[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const clearSelection = () => setSelectedIds(new Set());
    const [departmentId, setDepartmentId] = useState<number | "" | "all">("");
    const [jobLevelOptions, setJobLevelOptions] = useState<Option[]>([]);
    const [jobLevelId, setJobLevelId] = useState<number | "" | "all">("");
    const [jobTypeOptions, setJobTypeOptions] = useState<Option[]>([]);
    const [jobTypeId, setJobTypeId] = useState<number | "" | "all">("");
    const [searchText, setSearchText] = useState("");
    const debouncedSearch = useDebouncedValue(searchText, 400);
    const normalizedSearch = useMemo(() => normalizeSearch(debouncedSearch), [debouncedSearch]);

    const canDelete = groupPermissionAccessCMSCareer.delete()

    useEffect(() => {
        let alive = true;

        (async () => {
            try {
                const [deptRes, levelRes, typeRes] = await Promise.all([
                    getDepartmentIndexApi(),
                    getJobLevelIndexApi(),
                    getJobTypeIndexApi(),
                ]);
                if (!alive) return;

                {
                    const items = deptRes.data.data.items ?? [];
                    const opts: Option[] = [
                        { value: "all", label: "All" },
                        ...items
                            .filter(d => d.isActive)
                            .map(d => ({ value: d.id, label: d.name })),
                    ];
                    setDepartmentOptions(opts);
                }

                {
                    const items = levelRes.data.data.data.items ?? [];
                    const opts: Option[] = [
                        { value: "all", label: "All" },
                        ...items
                            .filter(x => x.isActive)
                            .sort((a, b) => a.rank - b.rank)
                            .map(x => ({ value: x.id, label: x.name })),
                    ];
                    setJobLevelOptions(opts);
                }

                {
                    const items = typeRes.data.data.items ?? [];
                    const opts: Option[] = [
                        { value: "all", label: "All" },
                        ...items
                            .filter(x => x.isActive)
                            .map(x => ({ value: x.id, label: x.name })),
                    ];
                    setJobTypeOptions(opts);
                }
            } catch (e) {
                console.error("GET filter options failed:", e);
            }
        })();

        return () => { alive = false; };
    }, []);

    useEffect(() => {
        let alive = true;

        const run = async () => {
            try {
                setLoading(true);

                const res = await getCareerSubmitApi(currentPage, rowsPerPage, {
                    department: departmentId === "" || departmentId === "all" ? undefined : departmentId,
                    level: jobLevelId === "" || jobLevelId === "all" ? undefined : jobLevelId,
                    jobType: jobTypeId === "" || jobTypeId === "all" ? undefined : jobTypeId,
                    search: normalizedSearch || undefined,
                });

                if (!alive) return;

                const payload = res?.data?.data;
                const items = payload?.items ?? [];
                const pagination = payload?.pagination;

                setTotalItems(pagination?.totalRecords ?? 0);

                const mapped: InboxRow[] = items.map((item, idx) => {
                    const sequentialId = (currentPage - 1) * rowsPerPage + idx + 1;

                    return {
                        id: item.applicationId,
                        ID: String(sequentialId),
                        "Candidate name": item.candidateName ?? "-",
                        Job: item.jobTitle ?? "-",
                        Experience: "-",
                        "Applied date": item.appliedAt ? new Date(item.appliedAt).toLocaleDateString() : "-",
                        Status: item.status ?? "-",
                    };
                });

                setRows(mapped);
            } catch (e) {
                console.error("GET career submit failed:", e);
            } finally {
                if (alive) setLoading(false);
            }
        };

        run();
        return () => {
            alive = false;
        };
    }, [
        currentPage,
        rowsPerPage,
        departmentId,
        jobLevelId,
        jobTypeId,
        normalizedSearch,
        reloadKey,
    ]);

    useEffect(() => {
        setCurrentPage(1);
    }, [rowsPerPage, departmentId, jobLevelId, jobTypeId, normalizedSearch]);

    function useDebouncedValue<T>(value: T, delay = 400) {
        const [debounced, setDebounced] = useState(value);

        useEffect(() => {
            const t = setTimeout(() => setDebounced(value), delay);
            return () => clearTimeout(t);
        }, [value, delay]);

        return debounced;
    }

    const openProfile = useCallback(async (applicationId: string) => {
        if (!applicationId) return;

        setPreviewLoading(true);

        ursb.open({
            title: "View submitted",
            widthSize: "400px",
            content: (
                <UrsbSubmit
                    applicationId={applicationId}
                    jobId="-"
                    applyFor="-"
                    email="-"
                    phone="-"
                    message="Loading..."
                    applyDate="-"
                    experiences="-"
                    status="new"
                    onBack={() => ursb.close()}
                />
            ),
        });

        try {
            const res = await getCareerSubmitPreviewApi(applicationId);
            const p = res.data.data;

            const file = p.attachments
                ? {
                    name: p.attachments.fileName,
                    sizeLabel: formatBytes(p.attachments.fileSize),
                    url: p.attachments.url,
                }
                : undefined;

            ursb.open({
                title: "View submitted",
                widthSize: "400px",
                content: (
                    <UrsbSubmit
                        applicationId={applicationId}
                        jobId={p.jobCode ?? "-"}
                        applyFor={p.jobTitle ?? "-"}
                        email={p.email ?? "-"}
                        phone={p.phone ?? "-"}
                        message="-"
                        applyDate={p.appliedAt ? new Date(p.appliedAt).toLocaleDateString() : "-"}
                        experiences={p.jobLevel ?? "-"}
                        status={p.status}
                        file={file}
                        onBack={() => ursb.close()}
                    />
                ),
            });

        } catch (e) {
            console.error("GET preview failed:", e);

            ursb.open({
                title: "View submitted",
                widthSize: "400px",
                content: (
                    <UrsbSubmit
                        applicationId={applicationId}
                        jobId="-"
                        applyFor="-"
                        email="-"
                        phone="-"
                        message="Failed to load"
                        applyDate="-"
                        experiences="-"
                        status="new"
                        onBack={() => ursb.close()}
                    />
                ),
            });
        } finally {
            setPreviewLoading(false);
        }
    }, [ursb]);

    const handleConfirmDelete = useCallback((clickedId?: string) => {
        const idsToDelete =
            selectedIds.size > 0
                ? Array.from(selectedIds)
                : clickedId
                    ? [clickedId]
                    : [];

        if (idsToDelete.length === 0) return;

        const count = idsToDelete.length;
        const isBatch = count > 1;

        openConfirm(
            <Confirmation
                visible
                title={
                    isBatch
                        ? `Delete ${count} selected applications?`
                        : "Delete application?"
                }
                description={
                    isBatch
                        ? `You are about to permanently delete ${count} selected application${count > 1 ? "s" : ""}. This action cannot be undone.`
                        : "You are about to permanently delete this application. This action cannot be undone."
                }
                status="error"
                onClose={closeConfirm}
                icon={<DeleteConfirmationIcon />}
                buttons={
                    <>
                        <Button
                            variant="ghost"
                            size="md"
                            value="Cancel"
                            type="button"
                            disabled={saving}
                            onClick={closeConfirm}
                        />

                        <Button
                            variant="danger"
                            size="md"
                            value={
                                saving
                                    ? "Deleting..."
                                    : isBatch
                                        ? `Delete`
                                        : "Delete"
                            }
                            type="button"
                            disabled={saving}
                            onClick={async () => {
                                try {
                                    setSaving(true);

                                    const res = isBatch
                                        ? await batchDeleteCareerApplications(idsToDelete)
                                        : await deleteInboxItem(idsToDelete[0]);

                                    const ok =
                                        (isBatch && (res?.status === 200 || res?.status === 204)) ||
                                        (!isBatch && res?.status === 204);

                                    if (ok) {
                                        showToast({
                                            status: "success",
                                            title: "Delete success",
                                            description: isBatch
                                                ? `${count} applications deleted successfully.`
                                                : "Application deleted successfully.",
                                        });
                                    } else {
                                        showToast({
                                            status: "warning",
                                            title: "Uncompleted!",
                                            description: "No applications were deleted.",
                                        });
                                    }

                                    closeConfirm();
                                    clearSelection();
                                    setReloadKey((k) => k + 1);
                                } catch (e) {
                                    showToast({
                                        status: "error",
                                        title: "Uncompleted!",
                                        description: "Application(s) cannot be deleted.",
                                    });
                                } finally {
                                    setSaving(false);
                                }
                            }}
                        />
                    </>
                }
            />
        );
    }, [selectedIds, saving, openConfirm, closeConfirm, showToast]);

    const formattedData = useMemo(
        () =>
            rows.map((row) => ({
                ...row,
                Status: <StatusBadge
                variant={toBadgeVariant(row.Status)}
                label={row.Status ? row.Status.charAt(0).toUpperCase() + row.Status.slice(1) : '-'}
                />,
                Action: (
                    <div className="scl--ibx-action-const-sty">
                        <button
                            className="scl--ibx-eye-btn-const-sty"
                            type="button"
                            onClick={() => openProfile(row.id)}
                        >
                            <EyeIcon />
                        </button>
                        {canDelete &&
                            <>
                                <span>|</span>
                                <button
                                    className="scl--ibx-tras-btn-const-sty"
                                    type="button"
                                    onClick={() => handleConfirmDelete(row.id)}
                                >
                                    <TrashIcon />
                                </button>
                            </>
                        }
                    </div>
                ),
            })),
        [rows, handleConfirmDelete, openProfile]
    );
    const hasActiveFilters = Boolean(
        (departmentId !== '' && departmentId !== 'all') ||
        (jobTypeId !== '' && jobTypeId !== 'all') ||
        (jobLevelId !== '' && jobLevelId !== 'all') ||
        searchText
    );

    const handleClearFilters = useCallback(() => {
        setDepartmentId('');
        setJobTypeId('');
        setJobLevelId('');
        setSearchText('');
        setCurrentPage(1);
    }, []);

    return (
        <main className='scl--submit-main-content'>
            <div className="scl--crr-smb-head-const-box">
                <MainBreadcrumb
                    title="Job submitted"
                    paragraph="View displays messages from other users as well as automated messages associated with scheduled actions."
                />
            </div>

            <div className="scl--crr-smb-filt-const-box">
                <div className="scl--crr-smb-filt-deparment">
                    <SelectField
                        name="department"
                        placeholder="Department"
                        label="Department"
                        options={departmentOptions}
                        value={departmentId}
                        onChange={(v) => {
                            let next: number | "" | "all" = "";

                            if (v == null || v === "") {
                                next = "";
                            } else if (v === "all") {
                                next = "all";
                            } else {
                                next = Number(v);
                            }

                            setDepartmentId(next);
                            setCurrentPage(1);
                        }}
                    />
                </div>

                <div className="scl--crr-smb-filt-status">
                    <SelectField
                        name="jobType"
                        placeholder="Job type"
                        label="Job type"
                        options={jobTypeOptions}
                        value={jobTypeId}
                        onChange={(v) => {
                            let next: number | "" | "all" = "";

                            if (v == null || v === "") {
                                next = "";
                            } else if (v === "all") {
                                next = "all";
                            } else {
                                next = Number(v);
                            }

                            setJobTypeId(next);
                            setCurrentPage(1);
                        }}
                    />
                </div>

                <div className="scl--crr-smb-filt-type">
                    <SelectField
                        name="jobLevel"
                        placeholder="Job level"
                        label="Job level"
                        options={jobLevelOptions}
                        value={jobLevelId}
                        onChange={(v) => {
                            let next: number | "" | "all" = "";

                            if (v == null || v === "") {
                                next = "";
                            } else if (v === "all") {
                                next = "all";
                            } else {
                                next = Number(v);
                            }

                            setJobLevelId(next);
                            setCurrentPage(1);
                        }}
                    />
                </div>

                <div className="scl--crr-smb-filt-search">
                    <SearchField
                        name="search"
                        placeholder="Search"
                        label="Search"
                        value={searchText}
                        onChange={(v) => setSearchText(v)}
                    />
                </div>
                {hasActiveFilters && (
                    <CancelButton onClick={handleClearFilters} aria-label="Clear all filters" />
                )}
            </div>

            <div className="scl--crr-smb-tabl-const-box">
                {rows.length === 0 && !loading ? (
                    <Empty
                        title="No Data yet"
                        description="There's nothing here yet. Start by adding new data to see it listed here."
                    />
                ) : (
                    <>
                        <Table
                            key={rows.map(r => `${r.id}:${r.Status}`).join("|")}
                            columns={columns}
                            data={formattedData}
                            showCheckbox
                            showId={false}
                            showPagination={false}
                            rowsPerPageOptions={[5, 10, 20]}
                            defaultRowsPerPage={10}
                            backgroundColor="transparent"
                            selectColumnWidth={49}
                            clickableColumns={["Candidate name"]}
                            onCellClick={(row: any) => { openProfile(row.id); }}
                            onSelectionChange={(selectedRowIds: string[]) => {
                                setSelectedIds(new Set(selectedRowIds));
                            }}
                            columnWidths={{
                                ID: "4%",
                                "Candidate name": "20%",
                                Job: "25%",
                                Experience: "10%",
                                "Applied date": "15%",
                                Status: "10%",
                                Action: "13%",
                            }}
                        />

                        <Pagination
                            rowsPerPage={rowsPerPage}
                            setRowsPerPage={setRowsPerPage}
                            rowsPerPageOptions={[5, 10, 20]}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            totalItems={totalItems}
                        />
                    </>
                )}

            </div>
        </main>
    )
}

export default SubmitUi